import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiAlertTriangle, FiCalendar, FiTruck, FiDollarSign } from 'react-icons/fi';
import tesoreriaService from '../services/tesoreriaService';
import Loading from '../components/Loading';
import './CuentasPorPagarPage.css';

const ensureArray = (v) => Array.isArray(v) ? v : [];

const CuentasPorPagarPage = () => {
  const [facturas, setFacturas] = useState([]);
  const [cronograma, setCronograma] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vista, setVista] = useState('lista');
  const [proveedorId, setProveedorId] = useState('');
  const [estadoCuenta, setEstadoCuenta] = useState(null);
  const [cuentasBancarias, setCuentasBancarias] = useState([]);
  const [modalPago, setModalPago] = useState(false);
  const [pagoForm, setPagoForm] = useState({
    cuenta_bancaria: '', metodo_pago: 'transferencia', numero_comprobante: '',
    fecha_pago: '', monto: '', glosa: '', documento: null,
  });

  useEffect(() => { cargarDatos(); }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const [facturasRes, cronoRes, cuentasRes] = await Promise.all([
        tesoreriaService.cuentasPorPagar.getAll(),
        tesoreriaService.cuentasPorPagar.getCronograma(),
        tesoreriaService.cuentasBancarias.getAll({ activo: true }),
      ]);
      setFacturas(ensureArray(facturasRes.data.results || facturasRes.data));
      setCronograma(cronoRes.data);
      setCuentasBancarias(ensureArray(cuentasRes.data.results || cuentasRes.data));
    } catch { toast.error('Error al cargar cuentas por pagar'); }
    finally { setLoading(false); }
  };

  const cargarEstadoCuenta = async (id) => {
    if (!id) return;
    try {
      const res = await tesoreriaService.cuentasPorPagar.getEstadoCuenta(id);
      setEstadoCuenta(res.data);
      setProveedorId(id);
    } catch { toast.error('Error al cargar estado de cuenta'); }
  };

  const abrirModalPago = (factura) => {
    setPagoForm({
      cuenta_bancaria: '',
      metodo_pago: 'transferencia',
      numero_comprobante: '',
      fecha_pago: new Date().toISOString().split('T')[0],
      monto: factura.saldo_pendiente || '',
      glosa: `Pago ${factura.numero_documento || ''} - ${factura.proveedor_nombre || ''}`,
      documento: factura,
    });
    setModalPago(true);
  };

  const handleCrearPago = async () => {
    const doc = pagoForm.documento;
    if (!pagoForm.cuenta_bancaria || !pagoForm.fecha_pago || !pagoForm.monto) {
      toast.error('Complete los campos obligatorios');
      return;
    }
    try {
      const data = {
        tipo: 'egreso',
        cuenta_bancaria: pagoForm.cuenta_bancaria,
        metodo_pago: pagoForm.metodo_pago,
        numero_comprobante: pagoForm.numero_comprobante,
        fecha_pago: pagoForm.fecha_pago,
        monto: pagoForm.monto,
        proveedor: doc.proveedor_id,
        glosa: pagoForm.glosa,
        detalles_pago: [{ documento_legal: doc.documento_id, monto_aplicado: pagoForm.monto }],
      };
      await tesoreriaService.pagos.create(data);
      toast.success('Pago creado exitosamente');
      setModalPago(false);
      cargarDatos();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear pago');
    }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(v || 0);

  const getDiasColor = (dias) => {
    if (dias <= 0) return '#059669';
    if (dias <= 30) return '#f59e0b';
    if (dias <= 60) return '#ea580c';
    return '#dc2626';
  };

  if (loading) return <Loading />;

  const totalPendiente = facturas.reduce((s, f) => s + parseFloat(f.saldo_pendiente || 0), 0);
  const facturasVencidas = facturas.filter(f => f.dias_vencido > 0);

  return (
    <div className="cxp-page">
      <div className="page-header">
        <h1>Cuentas por Pagar</h1>
        <div className="view-toggle">
          <button className={vista === 'lista' ? 'active' : ''} onClick={() => setVista('lista')}>Lista</button>
          <button className={vista === 'cronograma' ? 'active' : ''} onClick={() => setVista('cronograma')}>Cronograma</button>
          <button className={vista === 'proveedor' ? 'active' : ''} onClick={() => setVista('proveedor')}>Por Proveedor</button>
        </div>
      </div>

      <div className="estadisticas-grid">
        <div className="stat-card" style={{ borderLeftColor: '#dc2626' }}>
          <div className="stat-label">Total por Pagar</div>
          <div className="stat-value">{formatCurrency(totalPendiente)}</div>
        </div>
        <div className="stat-card" style={{ borderLeftColor: '#f59e0b' }}>
          <div className="stat-label">Facturas Vencidas</div>
          <div className="stat-value">{facturasVencidas.length}</div>
        </div>
        <div className="stat-card" style={{ borderLeftColor: '#3b82f6' }}>
          <div className="stat-label">Total Facturas</div>
          <div className="stat-value">{facturas.length}</div>
        </div>
      </div>

      {vista === 'lista' && (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>N° Documento</th>
                <th>Proveedor</th>
                <th>Fecha Emision</th>
                <th>Fecha Vencimiento</th>
                <th style={{ textAlign: 'right' }}>Monto Total</th>
                <th style={{ textAlign: 'right' }}>Pagado</th>
                <th style={{ textAlign: 'right' }}>Saldo</th>
                <th>Prioridad</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {facturas.length === 0 ? (
                <tr><td colSpan="9" className="empty-state"><p>No hay facturas pendientes de pago</p></td></tr>
              ) : facturas.map((f, i) => (
                <tr key={i}>
                  <td className="numero-doc">{f.numero_documento || f.numero || '-'}</td>
                  <td><FiTruck size={14} style={{ marginRight: 4 }} />{f.proveedor_nombre || f.proveedor?.nombre || '-'}</td>
                  <td>{f.fecha_emision || f.fecha}</td>
                  <td>{f.fecha_vencimiento}</td>
                  <td style={{ textAlign: 'right', fontFamily: "'Courier New', monospace" }}>{formatCurrency(f.monto_total)}</td>
                  <td style={{ textAlign: 'right', fontFamily: "'Courier New', monospace", color: '#059669' }}>{formatCurrency(f.monto_pagado || 0)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600, fontFamily: "'Courier New', monospace" }}>{formatCurrency(f.saldo_pendiente)}</td>
                  <td>
                    <span className="dias-badge" style={{ color: getDiasColor(f.dias_vencido || 0) }}>
                      {f.dias_vencido > 0 && <FiAlertTriangle size={14} />}
                      {f.dias_vencido > 0 ? `Vencido ${f.dias_vencido}d` : f.dias_vencido === 0 ? 'Hoy' : `${Math.abs(f.dias_vencido || 0)}d restantes`}
                    </span>
                  </td>
                  <td>
                    <button className="btn-pagar" onClick={() => abrirModalPago(f)}>
                      <FiDollarSign size={14} /> Pagar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {vista === 'cronograma' && cronograma && (
        <div className="cronograma-container">
          {[
            { label: 'Vencido', key: 'vencido', color: '#dc2626' },
            { label: 'Esta Semana', key: 'esta_semana', color: '#f59e0b' },
            { label: 'Este Mes', key: 'este_mes', color: '#3b82f6' },
            { label: 'Proximo Mes', key: 'proximo_mes', color: '#059669' },
          ].map(periodo => (
            <div key={periodo.key} className="cronograma-section">
              <h3 style={{ color: periodo.color }}><FiCalendar /> {periodo.label}</h3>
              {cronograma[periodo.key]?.length > 0 ? (
                <table className="data-table">
                  <thead><tr><th>Documento</th><th>Proveedor</th><th>Vencimiento</th><th style={{ textAlign: 'right' }}>Saldo</th></tr></thead>
                  <tbody>
                    {cronograma[periodo.key].map((f, i) => (
                      <tr key={i}>
                        <td className="numero-doc">{f.numero_documento || '-'}</td>
                        <td>{f.proveedor_nombre || '-'}</td>
                        <td>{f.fecha_vencimiento}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(f.saldo_pendiente)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <p className="text-muted">Sin facturas en este periodo</p>}
              <div className="cronograma-subtotal">
                Subtotal: <strong>{formatCurrency(cronograma[periodo.key]?.reduce((s, f) => s + parseFloat(f.saldo_pendiente || 0), 0) || 0)}</strong>
              </div>
            </div>
          ))}
        </div>
      )}

      {vista === 'proveedor' && (
        <div className="proveedor-container">
          <div className="form-group" style={{ maxWidth: 400, marginBottom: 24 }}>
            <label>Seleccionar Proveedor</label>
            <input type="number" className="input-filtro" placeholder="ID del proveedor" value={proveedorId} onChange={e => setProveedorId(e.target.value)} onKeyDown={e => e.key === 'Enter' && cargarEstadoCuenta(proveedorId)} />
            <button className="btn-primary" style={{ marginTop: 8 }} onClick={() => cargarEstadoCuenta(proveedorId)}>Consultar</button>
          </div>
          {estadoCuenta && (
            <div className="estado-cuenta-card">
              <h3><FiTruck /> {estadoCuenta.proveedor_nombre || 'Proveedor'}</h3>
              <div className="estado-cuenta-resumen">
                <div><span>Total Facturado:</span><strong>{formatCurrency(estadoCuenta.total_facturado)}</strong></div>
                <div><span>Total Pagado:</span><strong style={{ color: '#059669' }}>{formatCurrency(estadoCuenta.total_pagado)}</strong></div>
                <div><span>Saldo Pendiente:</span><strong style={{ color: '#dc2626' }}>{formatCurrency(estadoCuenta.saldo_pendiente)}</strong></div>
              </div>
              {estadoCuenta.facturas && (
                <table className="data-table" style={{ marginTop: 16 }}>
                  <thead><tr><th>Documento</th><th>Fecha</th><th style={{ textAlign: 'right' }}>Monto</th><th style={{ textAlign: 'right' }}>Pagado</th><th style={{ textAlign: 'right' }}>Saldo</th></tr></thead>
                  <tbody>
                    {estadoCuenta.facturas.map((f, i) => (
                      <tr key={i}>
                        <td>{f.numero_documento || '-'}</td>
                        <td>{f.fecha}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(f.monto_total)}</td>
                        <td style={{ textAlign: 'right', color: '#059669' }}>{formatCurrency(f.monto_pagado)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(f.saldo_pendiente)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}

      {modalPago && (
        <div className="modal-overlay" onClick={() => setModalPago(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>Pagar Documento</h2>
            <div className="pago-doc-info">
              <span><strong>Documento:</strong> {pagoForm.documento?.numero_documento}</span>
              <span><strong>Proveedor:</strong> {pagoForm.documento?.proveedor_nombre}</span>
              <span><strong>Saldo pendiente:</strong> {formatCurrency(pagoForm.documento?.saldo_pendiente)}</span>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Cuenta Bancaria *</label>
                <select value={pagoForm.cuenta_bancaria} onChange={e => setPagoForm({ ...pagoForm, cuenta_bancaria: e.target.value })}>
                  <option value="">Seleccionar...</option>
                  {cuentasBancarias.map(c => <option key={c.id} value={c.id}>{c.nombre} ({c.banco})</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Metodo de Pago *</label>
                <select value={pagoForm.metodo_pago} onChange={e => setPagoForm({ ...pagoForm, metodo_pago: e.target.value })}>
                  <option value="transferencia">Transferencia Bancaria</option>
                  <option value="cheque">Cheque</option>
                  <option value="efectivo">Efectivo</option>
                  <option value="tarjeta_credito">Tarjeta de Credito</option>
                  <option value="tarjeta_debito">Tarjeta de Debito</option>
                  <option value="vale_vista">Vale Vista</option>
                </select>
              </div>
              <div className="form-group">
                <label>N° Comprobante</label>
                <input value={pagoForm.numero_comprobante} onChange={e => setPagoForm({ ...pagoForm, numero_comprobante: e.target.value })} placeholder="N° transferencia, cheque..." />
              </div>
              <div className="form-group">
                <label>Fecha *</label>
                <input type="date" value={pagoForm.fecha_pago} onChange={e => setPagoForm({ ...pagoForm, fecha_pago: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Monto *</label>
                <input type="number" value={pagoForm.monto} onChange={e => setPagoForm({ ...pagoForm, monto: e.target.value })} placeholder="0" />
              </div>
              <div className="form-group" style={{ gridColumn: 'span 2' }}>
                <label>Glosa</label>
                <textarea value={pagoForm.glosa} onChange={e => setPagoForm({ ...pagoForm, glosa: e.target.value })} rows={2} placeholder="Descripcion del pago..." style={{ padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', resize: 'vertical' }} />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setModalPago(false)}>Cancelar</button>
              <button className="btn-primary" onClick={handleCrearPago}>Crear Pago</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CuentasPorPagarPage;
